import type { MindItemSearchResult } from '@kimuno/api-sdk/lib/types/responses/MindItem';
import type { ProductSearchResult } from '@kimuno/api-sdk/lib/types/responses/Product';
import { Box, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import _, { debounce } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { Tab } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { palette } from '../../../assets/styles/themes/tokens';
import { zIndex } from '../../../constants';
import {
	getPaginationSelector,
	getUnifiedPaginationSelector,
} from '../../../reducers/search.selectors';
import { selectUser } from '../../../reducers/user.selectors';
import { useNewUiSearchPagePadding } from '../../../routes/courses/CourseSearchPage/CourseSearchPage';
import { MasterLayoutContext } from '../../../state/MasterLayoutProvider';
import {
	type SearchFilters,
	SearchResultsTab as SearchResultsTabEnum,
} from '../../../types/search';
import type { SearchEventData } from '../../../utils/ampli';
import searchResultsStorageManager from '../../../utils/searchResultsStorageManager';
import useCardDimensions from '../../../utils/useCardDimensions';
import type { MuiCardCommonProps } from '../../cards/MuiCard/MuiCardBase/MuiCardBase';
import MuTabsNewUI from '../../common/MuTabsNewUI';
import type { ItemsTab } from '../../common/MuTabsNewUI/MuTabsNewUI';
import type { Alert } from '../../NotificationAlert/NotificationAlert';
import SearchResultsTab from './SearchResultsTabs';
import useSearchCompletedTracking from './useSearchCompletedTracking';

export type SearchResultsItemsTab = ItemsTab<SearchResultsTabEnum> & { loadMore: () => void };

interface SearchResultsProps {
	currentTab: SearchResultsTabEnum;
	requestFilters: SearchFilters;
	trackingPayload: SearchEventData;
	tabs: SearchResultsItemsTab[];
	setCurrentTab?: (tab: SearchResultsTabEnum) => void;
	searchTerm?: string;
	forceNewDesign?: boolean;
	onShowNotification?: (alert: Alert) => void;
	onRelatedTopicSelect?: (topic: string) => void;
	isRelatedTopicsTreatment?: boolean;
}

const mapCurrentTabToSearchStateKey = (currentTab: SearchResultsTabEnum) => {
	switch (currentTab) {
		case SearchResultsTabEnum.MINDITEMS:
			return 'mindItems';
		case SearchResultsTabEnum.PRODUCTS:
			return 'products';
		case SearchResultsTabEnum.VIDEOS:
			return 'videoMindItems';
		case SearchResultsTabEnum.COURSES:
			return 'courseMindItems';
		case SearchResultsTabEnum.ALL:
			return 'all';
	}
};

/**
 * SEAR-460: `useAiFilters` gates which tag source the card renders for
 * subject/schoolType/classYear so it matches the filter values the user picked:
 * - `true`  → AI tags only (CP sources omitted)
 * - `false` → CP tags only (AI sources omitted)
 * - `undefined` (dashboard carousels, courses — no search cohort) → both are
 *   passed and the card falls back AI-first-then-CP, i.e. today's behavior.
 * `aiMainTopic` is not one of the three switched fields and is always passed.
 *
 * CP `schoolTypes` is only populated in explicit control mode (`false`): the
 * card had no CP school-type source before SEAR-460, so passing it for the
 * `undefined` (dashboard/course) callers would surface a school-type chip they
 * never rendered. In AI mode the AI school type is used; in legacy mode it stays
 * AI-only, exactly as today.
 */
export const getCommonCardProps = (
	item: MindItemSearchResult | ProductSearchResult,
	useAiFilters?: boolean
): MuiCardCommonProps => {
	const includeCp = useAiFilters !== true;
	const includeAi = useAiFilters !== false;
	const includeCpSchoolType = useAiFilters === false;
	return {
		id: item.id,
		publisher: item.publisher || '',
		description: item.description || '',
		subjects: includeCp ? item.subjects : undefined,
		schoolTypes: includeCpSchoolType ? item.schoolTypes : undefined,
		cover: item.thumbnail,
		title: item.title,
		classYears: includeCp ? item.classYears : undefined,
		isNew: item.isNew,
		date: item.publisherReleaseDate,
		isReadOnly: item.isReadOnly,
		isPinned: item.isPinned,
		itemData: item,
		aiMainTopic: item.aiTags?.aiMainTopic || undefined,
		aiProductItemSubject: includeAi ? item.aiTags?.aiProductItemSubject || undefined : undefined,
		aiProductItemClassYears: includeAi
			? item.aiTags?.aiProductItemClassYears || undefined
			: undefined,
		aiProductItemSchoolType: includeAi
			? item.aiTags?.aiProductItemSchoolType || undefined
			: undefined,
	};
};

const debounceTime = 500;
const searchEngineVersion = 2;

const SearchResults: React.FC<SearchResultsProps> = (props) => {
	const theme = useTheme();
	const cardDimensions = useCardDimensions();
	const { mainContentContainer } = useContext(MasterLayoutContext);

	const mindItemsPagination = useSelector(getPaginationSelector('mindItems'));
	const videoMindItemsPagination = useSelector(getPaginationSelector('videoMindItems'));
	const courseMindItemsPagination = useSelector(getPaginationSelector('courseMindItems'));
	const productsPagination = useSelector(getPaginationSelector('products'));
	const unifiedPagination = useSelector(getUnifiedPaginationSelector);

	const user = useSelector(selectUser);
	const userActionConfig = {
		hideCollectionAction: user?.hideCollectionAction,
		hidePinAction: user?.hidePinAction,
	};

	const mapTabToPagination = useMemo(
		() => ({
			[SearchResultsTabEnum.MINDITEMS]: mindItemsPagination,
			[SearchResultsTabEnum.PRODUCTS]: productsPagination,
			[SearchResultsTabEnum.VIDEOS]: videoMindItemsPagination,
			[SearchResultsTabEnum.COURSES]: courseMindItemsPagination,
			[SearchResultsTabEnum.ALL]: unifiedPagination,
		}),
		[
			mindItemsPagination,
			productsPagination,
			videoMindItemsPagination,
			courseMindItemsPagination,
			unifiedPagination,
		]
	);

	const searchStateKey = mapCurrentTabToSearchStateKey(props.currentTab);
	const saveScrollProperties = useCallback(
		(currentTab: SearchResultsTabEnum) => {
			const scrollY = mainContentContainer?.scrollTop || 0;
			const tabKey = mapCurrentTabToSearchStateKey(currentTab);
			const offset = mapTabToPagination[currentTab].offset;
			searchResultsStorageManager.setSearchStateInstance(tabKey, offset, scrollY);
		},
		[mainContentContainer, mapTabToPagination]
	);

	useEffect(() => {
		const resetStorageManager = () => {
			searchResultsStorageManager.clearSearchState();
		};

		window.addEventListener('beforeunload', resetStorageManager);

		return () => {
			window.removeEventListener('beforeunload', resetStorageManager);
		};
	}, []);

	// Restore scroll position
	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (!mainContentContainer) return;

		const searchStateInstance = searchResultsStorageManager.getSearchStateInstance(searchStateKey);
		if (!_.isEmpty(searchStateInstance)) {
			const { scrollPosition } = searchStateInstance;
			mainContentContainer.scrollTo(0, scrollPosition);
		} else {
			mainContentContainer.scrollTo(0, 0);
		}
	}, [props.currentTab, mainContentContainer, searchStateKey]);

	// Track scroll position
	useEffect(() => {
		if (!mainContentContainer) return;

		const handleScroll = debounce(() => {
			saveScrollProperties(props.currentTab);
		}, debounceTime);

		mainContentContainer.addEventListener('scroll', handleScroll);

		return () => {
			handleScroll.cancel();
			mainContentContainer.removeEventListener('scroll', handleScroll);
		};
	}, [props.currentTab, mainContentContainer, saveScrollProperties]);

	useSearchCompletedTracking({
		currentTab: props.currentTab,
		requestFilters: props.requestFilters,
		trackingPayload: props.trackingPayload,
		paginationByTab: mapTabToPagination,
		searchTerm: props.searchTerm,
		isRelatedTopicsTreatment: props.isRelatedTopicsTreatment,
	});

	const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
		props.setCurrentTab && props.setCurrentTab(props.tabs[newValue].key);
	};

	const newUiSearchPagePadding = useNewUiSearchPagePadding();

	const searchResultsTabContainerSx = {
		position: 'sticky',
		zIndex: zIndex.searchResultsTab,
		background: palette.background.paperElevation1,
		top: 0,
	};

	return (
		<Box
			data-testid="new-ui-search-results"
			sx={{
				paddingX: 0,
				fontSize: '16px',
				[theme.breakpoints.down('mobile')]: {
					fontSize: '12px',
				},
			}}
		>
			<Box>
				<Tab.Container
					activeKey={props.currentTab}
					onSelect={(key) => props.setCurrentTab && props.setCurrentTab(key as SearchResultsTabEnum)}
				>
					<Box sx={searchResultsTabContainerSx}>
						<Box
							sx={{
								maxWidth: cardDimensions.desktop.maxWidth,
								marginX: 'auto',
								paddingX: 0,
							}}
						>
							<MuTabsNewUI tabs={props.tabs} handleChange={handleChange} value={props.currentTab} />
						</Box>
					</Box>
					{props.tabs.length > 1 && (
						<Divider
							sx={{
								position: 'relative',
							}}
						/>
					)}
					<Box
						sx={{
							maxWidth: cardDimensions.desktop.maxWidth,
							marginX: 'auto',
							paddingX: newUiSearchPagePadding,
						}}
					>
						<Box sx={{ marginX: '2px' }}>
							<Tab.Content>
								{props.tabs.map((tab) => (
									<SearchResultsTab
										key={tab.key}
										searchResultsTab={tab.key}
										searchTerm={props.searchTerm}
										requestFilters={props.requestFilters}
										searchEngineVersion={searchEngineVersion}
										loadMore={tab.loadMore}
										userActionConfig={userActionConfig}
										currentTab={props.currentTab}
										onShowNotification={props.onShowNotification}
										onRelatedTopicSelect={props.onRelatedTopicSelect}
									/>
								))}
							</Tab.Content>
						</Box>
					</Box>
				</Tab.Container>
			</Box>
		</Box>
	);
};

export default SearchResults;
